import { Button, Image } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import ExitIcon from "../../../assets/icons/ExitIcon";
import XIcon from "../../../assets/icons/XIcon";
import Avatar from "../../../assets/images/avatar.svg";
import {
  useGetGroupAttachmentsQuery,
  useGetGroupMemberQuery,
  useLeaveFromGroupMutation,
  useUpdateGroupNameMutation,
} from "../../../features/superAdmin/ticketService/ticketServiceApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { toast } from "../../../lib/toast";
import { GetGroupChatDetailsResponse } from "../../../types/mail";
import Loader from "../../ui/Loader";
import AddGroupPhotoModal from "./AddGroupPhotoModal";
import AddNewMemberModal from "./AddNewMemberModal";
import CollapseItems from "./CollapseItems";
import GroupNameUpdateModal from "./GroupNameUpdateModal";
import Member from "./Member";

export default function GroupChatInfo({
  setShowInfo,
  chatDetails,
}: {
  setShowInfo: any;
  chatDetails?: GetGroupChatDetailsResponse;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const [seeAll, setSeeAll] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupNameError, setGroupNameError] = useState(false);
  const {
    open: openUpdateModal,
    handleOpen: handleUpdateOpen,
    handleClose: handleUpdateClose,
  } = useModal<undefined, "UPDATE">();
  const {
    open: openAddModal,
    handleOpen: handleAddOpen,
    handleClose: handleAddClose,
  } = useModal<undefined, "ADD">();

  const {
    open: openAddPhotoModal,
    handleOpen: handleAddPhotoOpen,
    handleClose: handleAddPhotoClose,
  } = useModal<undefined, "ADD_PHOTO">();

  const { user } = useAppSelector((state) => state.auth);
  const {
    isLoading: isGetMemberLoading,
    isError: isGetMemberError,
    error: getMemberError,
    data: getMemberData,
  } = useGetGroupMemberQuery(chatId, {
    skip: !chatId,
  });
  const {
    isLoading: isGetAttachmentLoading,
    isError: isGetAttachmentError,
    error: getAttachmentError,
    data: getAttachmentData,
  } = useGetGroupAttachmentsQuery(chatId, {
    skip: !chatId,
  });
  const [
    lazyLeaveFromGroup,
    {
      isLoading: isGetLeaveLoading,
      isError: isGetLeaveError,
      error: getLeaveError,
      isSuccess: isLeaveSuccess,
    },
  ] = useLeaveFromGroupMutation();
  const [
    updateGroupName,
    {
      isLoading: isUpdateGroupNameLoading,
      isError: isUpdateGroupNameError,
      error: updateGroupNameError,
      isSuccess: isUpdateGroupNameSuccess,
    },
  ] = useUpdateGroupNameMutation();
  useError(isGetMemberError, getMemberError, "Get group member list fail");
  useError(
    isGetAttachmentError,
    getAttachmentError,
    "Get group attachment list fail",
  );
  useError(isGetLeaveError, getLeaveError, "Leave from group fail");
  useError(
    isUpdateGroupNameError,
    updateGroupNameError,
    "Group name update fail",
  );

  const memberList = getMemberData?.data?.slice(0, !seeAll ? 5 : undefined);

  const handleUpdateGroupName = () => {
    const newData = {
      group_name: groupName,
    };
    updateGroupName({
      data: newData,
      id: chatDetails?.data?.id,
    });
  };

  useEffect(() => {
    let path = "/user/communication/message?leave=true";
    if (user?.role === "super_admin" || user?.role === "Admin") {
      path = "/admin/communication/message?leave=true";
    }
    if (isLeaveSuccess) {
      navigate(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeaveSuccess, navigate]);

  useEffect(() => {
    if (isUpdateGroupNameSuccess) {
      toast("success", "Group name updated successfully!");
      setGroupName("");
      handleUpdateClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateGroupNameSuccess]);

  return (
    <>
      {openUpdateModal.type === "UPDATE" && (
        <GroupNameUpdateModal
          handleClose={handleUpdateClose}
          open={openUpdateModal.open}
          isLoading={isUpdateGroupNameLoading}
          handleUpdateGroupName={handleUpdateGroupName}
          groupName={groupName}
          setGroupName={setGroupName}
          groupNameError={groupNameError}
          setGroupNameError={setGroupNameError}
          originalGroupName={chatDetails?.data?.group_name || ""}
        />
      )}

      {openAddModal.type === "ADD" && (
        <AddNewMemberModal
          handleClose={handleAddClose}
          open={openAddModal.open}
          chatId={chatDetails?.data?.id}
        />
      )}

      {openAddPhotoModal.type === "ADD_PHOTO" && (
        <AddGroupPhotoModal
          handleClose={handleAddPhotoClose}
          open={openAddPhotoModal.open}
          chatId={chatDetails?.data?.id}
        />
      )}

      <div className="border !border-[#cccccc] rounded-md p-3 overflow-y-auto h-full">
        <div className="flex justify-between items-center">
          <span className="text-lg">Group Info</span>
          <span className="cursor-pointer" onClick={setShowInfo}>
            <XIcon />
          </span>
        </div>

        <div className="flex justify-center mt-4">
          <div className="relative">
            <Image
              src={chatDetails?.data?.photo || Avatar}
              className="rounded-full object-cover"
              alt="..."
              width={80}
              height={80}
            />
            <span
              className="absolute bottom-0 right-0 bg-white p-1 rounded-full shadow cursor-pointer"
              onClick={() => handleAddPhotoOpen({ type: "ADD_PHOTO" })}
            >
              <EditIcon />
            </span>
          </div>
        </div>

        <div className="flex justify-center gap-2 items-center text-lg mt-4">
          <span>{chatDetails?.data?.group_name}</span>
          <span
            className="text-[#5E718D] cursor-pointer"
            onClick={() => handleUpdateOpen({ type: "UPDATE" })}
          >
            <EditIcon />
          </span>
        </div>

        <div className="flex justify-between items-center my-4">
          <span className="font-medium text-base">
            Members ({getMemberData?.data?.length || 0})
          </span>
          <span
            className="text-[#455468] !text-sm cursor-pointer"
            onClick={() => setSeeAll(!seeAll)}
          >
            {seeAll ? "See Less" : "See All"}
          </span>
        </div>
        {/* add new member */}
        <div className="flex gap-2 items-center">
          <span
            className="bg-[#F0F3F9] p-3 rounded-full cursor-pointer"
            onClick={() => handleAddOpen({ type: "ADD" })}
          >
            <AddIcon />
          </span>
          <span>Add New Member</span>
        </div>
        {/* member */}
        <div className="mt-4">
          {isGetMemberLoading ? (
            <Loader />
          ) : (
            memberList?.map((member) => (
              <Member
                name={member?.name}
                img={member?.pro_pic}
                key={member?.id}
              />
            ))
          )}
        </div>

        <div className="text-base font-medium my-4">Shared Files</div>
        <CollapseItems
          isLoading={isGetAttachmentLoading}
          data={getAttachmentData?.data}
        />
        <Button
          className="flex items-center text-[#FF3838] bg-[#FFF5F4] border-[#FFA19B] justify-center py-3"
          block
          loading={isGetLeaveLoading}
          onClick={() => {
            lazyLeaveFromGroup({
              id: String(chatId),
              data: {
                userId: Number(user?.id),
              },
            });
          }}
        >
          <ExitIcon /> <span>Leave Group</span>
        </Button>
      </div>
    </>
  );
}
